import React, { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Building2, Users, UserSquare2, Package, Tags, Ticket, TrendingUp, Activity } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const COLORS = ['#2563EB', '#10B981', '#F59E0B', '#EF4444', '#6366F1', '#8B5CF6'];

function MetricCard({ title, value, icon: Icon, color = 'blue' }) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-emerald-50 text-emerald-600',
    amber: 'bg-amber-50 text-amber-600',
    purple: 'bg-purple-50 text-purple-600',
    slate: 'bg-slate-50 text-slate-600',
  };

  return (
    <Card className="p-6 border-slate-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500 mb-1">{title}</p>
          <p className="text-3xl font-bold text-slate-900">{value?.toLocaleString() || 0}</p>
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="w-6 h-6" />
        </div>
      </div>
    </Card>
  );
}

export default function SuperAdminDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/reports/super-admin/global');
      setStats(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-400">Carregando estatísticas...</div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-slate-400">Nenhuma estatística disponível</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
          <Activity className="w-6 h-6 text-blue-600" />
          Dashboard Global - Super Admin
        </h1>
        <p className="text-sm text-slate-500 mt-1">Visão geral de todas as empresas do sistema</p>
      </div>

      {/* Métricas Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Empresas Cadastradas"
          value={stats.totalCompanies}
          icon={Building2}
          color="blue"
        />
        <MetricCard
          title="Empresas Ativas"
          value={stats.activeCompanies}
          icon={TrendingUp}
          color="green"
        />
        <MetricCard
          title="Total de Usuários"
          value={stats.totalUsers}
          icon={Users}
          color="purple"
        />
        <MetricCard
          title="Total de Clientes"
          value={stats.totalCustomers}
          icon={UserSquare2}
          color="amber"
        />
      </div>

      {/* Segunda Linha de Métricas */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MetricCard
          title="Total de Tickets"
          value={stats.totalTickets}
          icon={Ticket}
          color="blue"
        />
        <MetricCard
          title="Total de Produtos"
          value={stats.totalProducts}
          icon={Package}
          color="green"
        />
        <MetricCard
          title="Total de Categorias"
          value={stats.totalCategories}
          icon={Tags}
          color="purple"
        />
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Empresas por Plano */}
        {stats.companiesByPlan && stats.companiesByPlan.length > 0 && (
          <Card className="p-6 border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Empresas por Plano</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stats.companiesByPlan}
                  dataKey="count"
                  nameKey="plan"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label
                >
                  {stats.companiesByPlan.map((_, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </Card>
        )}

        {/* Top Empresas por Tickets */}
        {stats.companiesStats && stats.companiesStats.length > 0 && (
          <Card className="p-6 border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Top Empresas por Tickets</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.companiesStats.slice(0, 10)}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis dataKey="companyName" angle={-45} textAnchor="end" height={100} tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="totalTickets" fill="#2563EB" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        )}
      </div>

      {/* Tabela de Empresas */}
      {stats.companiesStats && stats.companiesStats.length > 0 && (
        <Card className="border-slate-200">
          <div className="p-4 border-b border-slate-200">
            <h3 className="text-lg font-semibold text-slate-900">Detalhamento por Empresa</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-left">
                <tr>
                  <th className="px-4 py-3 font-medium">Empresa</th>
                  <th className="px-4 py-3 font-medium">Plano</th>
                  <th className="px-4 py-3 font-medium text-center">Status</th>
                  <th className="px-4 py-3 font-medium text-right">Usuários</th>
                  <th className="px-4 py-3 font-medium text-right">Clientes</th>
                  <th className="px-4 py-3 font-medium text-right">Produtos</th>
                  <th className="px-4 py-3 font-medium text-right">Categorias</th>
                  <th className="px-4 py-3 font-medium text-right">Tickets</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {stats.companiesStats.map((company) => (
                  <tr key={company.companyId} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium text-slate-900">{company.companyName}</td>
                    <td className="px-4 py-3 text-slate-700">
                      <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-700">
                        {company.plan || 'N/A'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        company.isActive 
                          ? 'bg-emerald-100 text-emerald-700' 
                          : 'bg-slate-100 text-slate-700'
                      }`}>
                        {company.isActive ? 'Ativa' : 'Inativa'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">{company.totalUsers}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{company.totalCustomers}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{company.totalProducts}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{company.totalCategories}</td>
                    <td className="px-4 py-3 text-right font-semibold text-slate-900">{company.totalTickets}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
